// @ts-strict-ignore
import React, { useContext, useState } from 'react';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';
import {
  selectActions,
  selectNodeLineageForAnchorNode,
  selectRuleBuilderActiveSubEditor,
  selectRuleBuilderPanelBoxContainerWidth,
  selectWorkflow,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';
import { RuleBuilderSubEditorSide, RuleBuilderSubEditorType } from 'flow/store/engine/types';
import {
  buildActionOptions,
  buildAllAttributeOptions,
  computeRuleItemLengthLimit,
  getMultiSelectProps,
  getSelectedOption,
  getShouldHideTooltip,
} from './helpers';
import { ActionType } from 'flow/store/engine/constants';
import type { DropdownOption } from 'flow/editor/components/NodeEditorDrawer/types';
import { updateThreshold } from 'flow/store/redux/thunks/thresholdUpdates';
import { isActionOfType } from 'flow/service/actions';
import { RuleBuilderContext } from '../../context';
import { ThresholdContext } from './context';
import { AGGREGATION_EDIT_OPTION_LABEL, MLA_ATTRIBUTE_EDIT_OPTION_LABEL } from './constants';
import RuleMultiSelect from '../RuleTemplate/RuleMultiSelect';
import { setRuleBuilderActiveSubEditor } from 'flow/store/redux/slice';
import { getIsPreview } from '../../helpers';
import { StyledEditableControlContainer } from '../styles';
import { Tooltip } from '@usealloy/component-library';
import { MLAType } from 'flow/service/mla';
import MLASuperset from './MLASuperset';
import { doesActionSupportTooltip } from 'flow/editor/components/Graph/nodes/BaseNode/helpers';
import {
  getAggregationName,
} from '~/dashboard/views/workflows/service/services';

const LHSWrapper = () => {
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
  const dispatch = useAppDispatch();
  const {
    keyboardFocus,
    sourceAction,
    editMode,
    ActionHoverTooltip,
  } = useContext(RuleBuilderContext);
  const { populatedThreshold } = useContext(ThresholdContext);
  const actions = useAppSelector(selectActions);
  const { services } = useAppSelector(selectWorkflowProps);
  const nodeLineage = useAppSelector(selectNodeLineageForAnchorNode);
  const activeSubEditor = useAppSelector(selectRuleBuilderActiveSubEditor);
  const workflow = useAppSelector(selectWorkflow);
  const ruleEditorPanelBoxContainerWidth = useAppSelector(selectRuleBuilderPanelBoxContainerWidth);
  const lengthLimit = computeRuleItemLengthLimit(ruleEditorPanelBoxContainerWidth);

  const { left, publicId } = populatedThreshold;

  const options = buildActionOptions({
    actions,
    sourceAction,
    services,
    nodeLineage,
    lengthLimit,
    thresholdSide: left,
  });

  const selectedOption = getSelectedOption({
    incomingValue: left.action.publicId,
    options,
  });

  const aggregationName = getAggregationName(left);

  const isServiceAction = isActionOfType(left.action, ActionType.SERVICE);

  const attributeOptions = isServiceAction
    ? buildAllAttributeOptions({
      service: left.service,
      workflow,
      currentAttribute: {
        attributeId: left.attributeId,
        attribute: left.attribute,
      },
      aggregationDisplayName: aggregationName,
    })
    : [];

  const selectedAttributeOption = isServiceAction
    ? getSelectedOption({
      incomingValue: String(left.attributeId),
      options: attributeOptions,
    })
    : null;

  const handleChange = (option: DropdownOption) => {
    dispatch(updateThreshold.leftAction(publicId, option.value));
  };

  const handleAttributeChange = (option: DropdownOption) => {
    if (option.label === MLA_ATTRIBUTE_EDIT_OPTION_LABEL) {
      dispatch(
        setRuleBuilderActiveSubEditor({
          thresholdId: populatedThreshold.publicId,
          type: RuleBuilderSubEditorType.MLA_EDITOR,
          side: RuleBuilderSubEditorSide.LHS,
        }),
      );
      return;
    }

    if (option.label === AGGREGATION_EDIT_OPTION_LABEL) {
      keyboardFocus.disable();
      dispatch(
        setRuleBuilderActiveSubEditor({
          thresholdId: populatedThreshold.publicId,
          type: RuleBuilderSubEditorType.AGGREGATIONS_EDITOR,
          side: RuleBuilderSubEditorSide.LHS,
        }),
      );
      return;
    }

    dispatch(updateThreshold.leftAttribute(publicId, Number(option.value)));
  };

  const multiSelectProps = getMultiSelectProps({
    action: left.action,
    options: {
      isPreview: getIsPreview(editMode, populatedThreshold, activeSubEditor.thresholdId),
    },
  });

  const tooltipContent = ActionHoverTooltip
    && doesActionSupportTooltip(left.action) ? (
      <ActionHoverTooltip key={left.action.publicId} action={left.action} />
    ) : null;

  return (
    <>
      <Tooltip
        align="start"
        content={tooltipContent}
        side="top"
        variant="secondary"
        disabled={(
          !tooltipContent
          || getShouldHideTooltip(selectedOption)
          || isActionMenuOpen
        )}
        delayDuration={0}
      >
        <StyledEditableControlContainer>
          <RuleMultiSelect
            {...multiSelectProps}
            options={options}
            onChange={handleChange}
            selectedOptions={selectedOption}
            noOptionsMessage={() => 'No options'}
            isMulti={false}
            isNaked
            isMenuOpen={setIsActionMenuOpen}
          />
        </StyledEditableControlContainer>
      </Tooltip>
      {isServiceAction && selectedAttributeOption && (
        <StyledEditableControlContainer>
          <RuleMultiSelect
            {...multiSelectProps}
            options={attributeOptions}
            onChange={handleAttributeChange}
            selectedOptions={selectedAttributeOption}
            isMulti={false}
            isNaked
          />
        </StyledEditableControlContainer>
      )}
      <MLASuperset type={MLAType.THRESHOLD_LEFT} />
    </>
  );
};

export default LHSWrapper;
